import { type ApiResponse, TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useEffect, useRef } from 'react'

import { queryClient } from 'v2/configs/rq-setup'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import type { RLanguage } from '../sidebar/types'
import { sortLanguages } from '../utils/lang-utils'
import type {
  AddDocumentPayload,
  CreateKBPayload,
  RKnowledgeBase,
  SyncFrequencyPeriod,
} from './types'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

export const useCrawlStatus = (jobId?: string) =>
  useQuery({
    queryKey: flowQueryKeys().crawlStatus(jobId as string),
    queryFn: () => flowService.getCrawlSiteStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: 5000,
    staleTime: 15000,
    gcTime: 5000,
  })

// Stop an in-progress crawl Preview (rag-service cancels the Firecrawl job). Pages already streamed
// back are kept; the caller clears its loading state.
export const useCancelCrawl = () =>
  useMutation({
    mutationFn: (jobId: string) => flowService.cancelCrawl(jobId),
  })

export const useCreateKBMutation = () => {
  const navigate = useAppNavigate()
  return useMutation({
    mutationKey: flowQueryKeys().createKnowledgeBase,
    mutationFn: async (data: CreateKBPayload) => {
      const response = await flowService.createKnowledgeBase(data)
      return response.data
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
      navigate(`/agent/knowledge-base`)
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create knowledge base',
        description: `${getErrorText(error as AxiosError)}. Please try again later.`,
      })
    },
  })
}

type Select<T, O> = (data?: T) => O

export const useGetKnowledgeBases = <TOutput = RKnowledgeBase[]>(
  select?: Select<RKnowledgeBase[], TOutput>,
) => {
  return useQuery({
    queryKey: flowQueryKeys().knowledgeBases,
    queryFn: async () => {
      const response = await flowService.listKnowledgeBases()
      return response.data
    },
    select,
    // The KB list is read by several mounted consumers at once (agent tab, link popover, vibe agent
    // selector). Without staleTime they each trigger a fetch on mount → duplicate network calls for
    // the same data. 30s keeps it shared/deduped across those mounts.
    staleTime: 30 * 1000,
    // Evict on unmount so the poll backstop below (dataUpdateCount) resets per visit. Without this the
    // count persists across navigate-away-and-return within the default 5m gcTime, so the backstop
    // could trip early and freeze a still-syncing KB on stale status. Mirrors useGetKnowledgeBase.
    gcTime: 0,
    // Live-refresh KB statuses while the list is on screen: poll ONLY while at least one KB is still
    // ingesting, and STOP the instant every KB is terminal (Active/Failed) — so an idle list issues
    // zero requests (no system overload). A hard backstop caps a permanently-wedged KB so it can
    // never poll forever. React Query stops this automatically when the page unmounts (no observers),
    // so it never runs in the background — it's scoped to "only when on the page".
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false
      const kbs = query.state.data
      if (!kbs?.length) return false
      // This poll already STOPS the instant no KB is in-flight (the anyInFlight check below), so the
      // numeric cap is only a "never poll forever on a genuinely wedged KB" backstop — NOT a sync-length
      // limit. The list summary carries no per-KB progress signal (no chunk_count), so unlike the detail
      // poller we can't do a stall detector here; instead bound it generously. A whole-Confluence sync
      // runs 60-80 min; at 8s that's ~600 ticks, so the old `> 60` (~8 min) cap stopped a healthy list
      // from refreshing mid-sync. 900 ticks ≈ 2 h covers it with margin while still capping a wedged KB.
      if (query.state.dataUpdateCount > 900) return false
      const anyInFlight = kbs.some((kb) => {
        const s = kb.embedding_status
        return !!s && !s.includes('completed') && !s.includes('failed')
      })
      return anyInFlight ? 8000 : false
    },
  })
}

export const useGetKnowledgeBase = (id: string) => {
  // Progress-aware poll backstop. A big sync (e.g. an 11k-page Confluence workspace) ingests for many
  // minutes — 60-80 min has been measured — so we must NOT stop polling just because a fixed number of
  // ticks elapsed. The previous `dataUpdateCount > 40` cap killed polling ~3.3 min in (40 × 5 s), which
  // is why a whole-source sync looked like it "jumped pending → active" and the doc count only grew on a
  // manual refresh: polling had already died while the backend was still healthily embedding. Instead,
  // track a signature of REAL progress (status + #documents discovered + #chunks embedded) and only give
  // up after a sustained STALL — ~3 min with zero change, i.e. a genuinely wedged/never-resolving source.
  const stallRef = useRef<{ sig: string; stalls: number }>({ sig: '', stalls: 0 })
  // Reset the stall tracker when the viewed KB changes (defensive: the detail route normally unmounts on
  // navigation, but if this hook is ever reused with a changing id the ref would carry the previous KB's
  // stall count/signature and could stop a fresh sync's polling early).
  useEffect(() => {
    stallRef.current = { sig: '', stalls: 0 }
  }, [id])
  return useQuery<RKnowledgeBase>({
    queryKey: flowQueryKeys().knowledgeBase(id),
    queryFn: async () => {
      const response = await flowService.getKnowledgeBase(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (data) => {
      if (data.state.status === 'error') return false
      const kb = data.state.data
      if (kb == null) return false
      const status = kb.embedding_status
      // Stop ONLY on a terminal embedding status (reset the stall tracker so a later re-sync starts
      // fresh). Don't stop on transiently-empty sources — a brand-new KB has sources=[] for a tick; the
      // stall backstop below bounds that wait without freezing the page on "no sources".
      if (status && (status.includes('completed') || status.includes('failed'))) {
        stallRef.current = { sig: '', stalls: 0 }
        return false
      }
      // Progress signature: anything climbing resets the stall counter so a healthy long sync keeps
      // polling. CRITICAL: the per-source documents[] is CAPPED (top-50, errored-first), so once the
      // first 50 pages embed it stops changing while pages 51..N ingest off-screen — relying on it alone
      // would re-freeze polling mid-sync. The KB-level `chunk_count` (summed across sources, UNCAPPED) is
      // the monotonic signal that keeps climbing for the whole sync; the capped doc fields stay as extra
      // signals (and cover the small-KB case where chunk_count may not be wired).
      const docs = (kb.sources ?? []).flatMap((s) => s.documents ?? [])
      const sig = `${status ?? ''}|${kb.chunk_count ?? 0}|${docs.length}|${docs.reduce((n, d) => n + (d.chunk_count ?? 0), 0)}`
      const st = stallRef.current
      if (sig !== st.sig) {
        st.sig = sig
        st.stalls = 0
      } else {
        st.stalls += 1
      }
      // ~3.3 min (40 ticks @ 5 s) with NO progress AND a non-terminal status → treat as wedged and stop
      // hammering (the original "never poll forever" guard, now keyed to stall instead of elapsed ticks).
      if (st.stalls > 40) return false
      return 5000
    },
  })
}

type LanguagesApiResponse = ApiResponse<{ languages: RLanguage[] }>

export const useGetLanguages = <TOutput = LanguagesApiResponse>(
  select?: (data: LanguagesApiResponse) => TOutput,
) =>
  useQuery({
    staleTime: convertToMs(20, TimeUnits.Minutes),
    queryKey: flowQueryKeys().languages,
    queryFn: async (): Promise<LanguagesApiResponse> => {
      const response = await flowService.getLanguages()
      const sortedLanguages = sortLanguages(response.data.languages)
      return {
        ...response,
        data: {
          ...response.data,
          languages: sortedLanguages,
        },
      }
    },
    select,
  })

export const useGetProviders = (languages?: string) =>
  useQuery({
    queryKey: flowQueryKeys().providers(languages),
    queryFn: () => flowService.getProviders(languages),
    staleTime: convertToMs(10, TimeUnits.Minutes),
  })

export const useKBMutations = () => {
  const edit = useMutation({
    mutationFn: async ({
      id,
      name,
      phlo_uuids,
      sync_frequency,
      sync_period,
      sync_enabled,
      include_image_content,
      include_youtube_content,
    }: {
      id: string
      name: string
      phlo_uuids: string[]
      sync_frequency: number
      sync_period: SyncFrequencyPeriod
      sync_enabled: boolean
      // Optional: rag PATCH supports changing the enrichment prefs after create (they apply to the
      // next sync). The edit modal now exposes these, so forward them when provided.
      include_image_content?: boolean
      include_youtube_content?: boolean
    }) => {
      await flowService.updateKnowledgeBase(
        {
          name,
          phlo_uuids,
          sync_frequency,
          sync_period,
          sync_enabled,
          include_image_content,
          include_youtube_content,
        },
        id,
      )
    },
    onSuccess: (_data, { id }) => {
      successToast({
        title: 'Knowledge Base updated',
        description: 'Knowledge base has been updated successfully.',
      })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
      // The edit modal changes the KB's name/sync settings; refresh the DETAIL too (not just the list),
      // else a KB opened from its detail page keeps showing the pre-edit values until a manual reload.
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(id) })
    },
    onError: (error) => {
      errorToast({ title: 'Failed to update', description: getErrorText(error as AxiosError) })
    },
  })

  const toggleStatus = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      await flowService.updateKnowledgeBase({ enabled }, id)
    },
    onSuccess: (_, { enabled }) => {
      successToast({
        title: `Knowledge Base ${enabled ? 'enabled' : 'disabled'}`,
        description: `Knowledge base has been ${enabled ? 'enabled' : 'disabled'} successfully.`,
      })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to update status',
        description: getErrorText(error as AxiosError),
      })
    },
  })

  const deleteKB = useMutation({
    mutationFn: async (id: string) => {
      await flowService.deleteKnowledgeBase(id)
    },
    onSuccess: () => {
      successToast({
        title: 'Knowledge Base deleted',
        description: 'Knowledge base has been deleted successfully.',
      })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
    },
    onError: (error) => {
      errorToast({ title: 'Failed to delete', description: getErrorText(error as AxiosError) })
    },
  })

  return { edit, toggleStatus, deleteKB }
}

export const useAddDocumentMutation = (id: string) =>
  useMutation({
    mutationFn: async (data: AddDocumentPayload) => {
      await flowService.addDocument(data, id)
    },
    onSuccess: () => {
      successToast({
        title: 'Document added',
        description: 'Document has been added successfully.',
      })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(id) })
      // Also refetch the KB lists (prefix-matched: org list incl. engine-keyed entry + agent tab):
      // adding a document flips the KB to ingesting, and the list poll only starts when the CACHED
      // list shows a KB in flight — without this the list said "Active" and never polled.
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
      window.cx.navigate(`/agent/knowledge-base/${id}`)
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to add document',
        description: getErrorText(error as AxiosError),
      })
    },
  })

export const useFetchDocumentsFromSource = (source: string, sourceId: string) =>
  useQuery({
    queryKey: flowQueryKeys().documentsFromSource(source, sourceId),
    queryFn: () => flowService.getDocumentsFromSource(source, sourceId),
    enabled: !!source && !!sourceId,
    // Reuse the fetch within a KB-creation session. With staleTime:0 every remount (e.g. stepping back
    // to "Setup" and forward to "Data source") RE-INITIATED the whole source fetch — a fresh fetch_id +
    // a full re-pull of every page (minutes on a large Confluence space). Cache it for the session so
    // navigating steps is instant; switching the app source clears it (handleAppChange.removeQueries).
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  })

export const useFetchDocumentsFromSourceByFetchId = (fetchId: string) =>
  useQuery({
    queryKey: flowQueryKeys().documentsFromSourceByFetchId(fetchId),
    queryFn: () => flowService.getDocumentsFromSourceByFetchId(fetchId),
    enabled: !!fetchId,
    // Keep the COMPLETED document tree cached across step navigation so returning to "Data source"
    // shows the already-fetched pages instantly instead of re-polling. Polling still runs while a fetch
    // is in flight (refetchInterval below); once 'completed' it stops and this cache serves remounts.
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    select: (data) => {
      if (!data?.data) return data
      const { status } = data.data

      // Show error toast and clear the tree if status is 'errored'
      if (status === 'errored') {
        errorToast({
          title: 'Document fetch failed',
          description: 'Failed to fetch documents from source. Please try again.',
        })
        // clear the real tree (data.data.data) — the previous `documents: []` set a non-existent
        // field, leaving the stale/partial tree in the cache on error.
        const clearedData = { ...data.data, data: [] }
        return { ...data, data: clearedData }
      }

      return data
    },
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status

      // Stop polling if status is 'errored' or 'failed'
      if (status === 'errored') {
        return false
      }

      // Stop polling when status is 'completed'
      if (status === 'completed') {
        return false
      }

      // Continue polling every 2 seconds for loading and other statuses
      return 2000
    },
  })

// Lazy picker: an imperative fetcher for ONE collection's (e.g. Confluence space's) pages, called on
// expand. queryClient.fetchQuery caches per collection so re-expanding a space is instant and never
// refetches. Returns the article array (already mapped to the console McpArticle shape).
export const useFetchCollectionArticles = (source: string, sourceId: string) =>
  useCallback(
    (collectionId: string) =>
      queryClient.fetchQuery({
        queryKey: [
          ...flowQueryKeys().documentsFromSource(source, sourceId),
          'collection',
          collectionId,
        ],
        queryFn: () => flowService.getCollectionArticles(source, collectionId, sourceId),
        staleTime: 10 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
      }),
    [source, sourceId],
  )

export const useFetchDocumentFromSource = (source: string, sourceId: string, documentId: string) =>
  useQuery({
    queryKey: flowQueryKeys().documentFromSource(source, sourceId, documentId),
    queryFn: () => flowService.getDocumentFromSource(source, sourceId, documentId),
    enabled: !!source && !!sourceId && !!documentId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5000,
  })
